import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Button from './Button';
import { reviewsService } from '../../services';
import styles from '../../styles/components/ReviewPromptCard.module.css';

/**
 * ReviewPromptCard
 * Einheitliche Bewertungsaufforderung nach Kauf — nutzt auf CheckoutSuccess & Purchases.
 *
 * Zustände:
 *  - Bereits bewertet: Zeigt kompakte Bestätigung mit Sternen und Link "Produkt öffnen"
 *  - Noch nicht bewertet: Call-to-Action mit ausklappbarem Sterne/Kommentar-Formular
 *  - Nach Submit: Success-State
 *
 * Props:
 *  - productId (number, required)
 *  - productTitle (string) - optional
 *  - productUrl (string) - optional Link zum Produkt
 *  - existingReview ({ rating, comment, createdAt }) - wenn bereits bewertet
 *  - onSubmitted(review) - Callback nach erfolgreichem Submit
 *  - variant: 'inline' (in Listen) | 'standalone' (prominente Karte, default)
 */
function ReviewPromptCard({
  productId,
  productTitle,
  productUrl,
  existingReview,
  onSubmitted,
  variant = 'standalone'
}) {
  const { t } = useTranslation();
  const [review, setReview] = useState(existingReview || null);
  const [expanded, setExpanded] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Bewertungs-Hinweise als Array (Index 0 = Platzhalter, 1-5 = Sterne-Texte)
  const ratingHints = [
    '',
    t('reviewPromptCard.ratingHint1'),
    t('reviewPromptCard.ratingHint2'),
    t('reviewPromptCard.ratingHint3'),
    t('reviewPromptCard.ratingHint4'),
    t('reviewPromptCard.ratingHint5'),
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!rating || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await reviewsService.submitReview(productId, { rating, comment });
      const next = {
        id: created?.id,
        rating: created?.rating ?? rating,
        comment: created?.comment ?? comment,
        createdAt: created?.created_at ?? new Date().toISOString()
      };
      setReview(next);
      setExpanded(false);
      setRating(0);
      setComment('');
      onSubmitted?.(next);
    } catch (err) {
      const msg = err?.data?.error
        || err?.message
        || t('reviewPromptCard.submitError');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  // Bereits bewertet
  if (review) {
    return (
      <div className={`${styles.card} ${styles.cardSubmitted} ${styles[variant]}`}>
        <div className={styles.submittedIcon} aria-hidden="true">
          <Icon name="checkCircle" size="md" />
        </div>
        <div className={styles.submittedBody}>
          <p className={styles.submittedTitle}>{t('reviewPromptCard.submittedTitle')}</p>
          <div
            className={styles.submittedStars}
            aria-label={t('reviewPromptCard.starsAriaLabel', { count: review.rating })}
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <Icon
                key={star}
                name="star"
                size="sm"
                className={star <= review.rating ? styles.starFilled : styles.starEmpty}
              />
            ))}
            {review.createdAt && (
              <span className={styles.submittedDate}>{formatDate(review.createdAt)}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Noch nicht bewertet - CTA + expandierbares Formular
  return (
    <div className={`${styles.card} ${styles[variant]}`}>
      {!expanded ? (
        <div className={styles.prompt}>
          <div className={styles.promptIcon} aria-hidden="true">
            <Icon name="star" size="md" />
          </div>
          <div className={styles.promptBody}>
            <p className={styles.promptTitle}>
              {productTitle
                ? t('reviewPromptCard.promptTitle', { title: `„${productTitle}"` })
                : t('reviewPromptCard.promptTitleNoProduct')}
            </p>
            <p className={styles.promptSubtitle}>{t('reviewPromptCard.promptSubtitle')}</p>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => setExpanded(true)}
            icon={<Icon name="star" size="sm" />}
          >
            {t('reviewPromptCard.rateBtnLabel')}
          </Button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit}>
          <p className={styles.formLabel}>
            {t('reviewPromptCard.formLabel')}
            {productTitle ? (
              <span className={styles.formProduct}>
                {' '}{t('reviewPromptCard.formFor')} „{productTitle}"
              </span>
            ) : null}
          </p>

          <div
            className={styles.starSelector}
            onMouseLeave={() => setHoverRating(0)}
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`${styles.starButton} ${star <= (hoverRating || rating) ? styles.starButtonActive : ''}`}
                onMouseEnter={() => setHoverRating(star)}
                onClick={() => setRating(star)}
                aria-label={t('reviewPromptCard.starAriaLabel', { count: star })}
                aria-pressed={rating === star}
              >
                <Icon name="star" size="lg" />
              </button>
            ))}
            {(hoverRating || rating) > 0 && (
              <span className={styles.ratingHint}>
                {ratingHints[hoverRating || rating]}
              </span>
            )}
          </div>

          <textarea
            className={styles.textarea}
            placeholder={t('reviewPromptCard.textareaPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
          />
          <div className={styles.meta}>
            <span className={styles.charCount}>{t('reviewPromptCard.charCount', { count: comment.length })}</span>
            {productUrl && (
              <a href={productUrl} className={styles.previewLink} target="_blank" rel="noreferrer">
                {t('reviewPromptCard.previewLink')}
              </a>
            )}
          </div>

          {submitError && (
            <p className={styles.error}>
              <Icon name="alertCircle" size="sm" />
              {submitError}
            </p>
          )}

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => {
                setExpanded(false);
                setSubmitError(null);
              }}
              disabled={submitting}
            >
              {t('reviewPromptCard.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="small"
              disabled={!rating || submitting}
              loading={submitting}
            >
              {t('reviewPromptCard.submit')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ReviewPromptCard;
